import { DSVRowArray } from 'd3';
import { DashboardDataTransformer } from '../../data_transformers/dashboard_data_transformer';
import { SingleSelectParentSelectChildFilterSettings } from '../../filters/dashboard_filter_parent_filter_settings';
import {
  DashboardSingleSelectFilter,
  SingleSelectFilterSettings,
  SingleSelectFilterUISettings,
} from '../../filters/dashboard_select_filter';
import { Metric } from '../dashboard-config.model';
import { KPI_BY_ID, KpiId, METRIC_ID_FILTER_NAME, selectedKpi } from './kpi-options';

/** Filter holding the selected index indicator ("Indizierter Wert"). */
export const INDICATOR_FILTER_NAME = 'indicator';

/** Pre-remodel filter name of each metric's indicator (saved slices, legacy dashboards). */
export const LEGACY_INDICATOR_FILTERS: Readonly<Partial<Record<Metric, string>>> = {
  [Metric.CompaniesRevenue]: 'revenue-index',
  [Metric.GrossValueAdded]: 'gross-value-added-index',
  [Metric.EconomicParticipant]: 'economic-participants-index',
  [Metric.Employed]: 'employed-index',
};

export const LEGACY_INDICATOR_FILTER_NAMES: readonly string[] = Object.values(LEGACY_INDICATOR_FILTERS);

/**
 * One index indicator ("Indizierter Wert") of a metric. Values are the legacy index
 * filter values, so saved slices only need the filter name aliased.
 */
export interface IndexIndicatorOption {
  value: string;
  metricId: Metric;
  /** Dropdown, export metadata and library slice title. */
  label: string;
  /** Word used in chart headings. */
  headingLabel: string;
  /** Dataset column, when the indicator alone picks it. */
  column?: string;
  /** Suffix joined to the selected KPI (`employed_total` + `_per_company`). */
  kpiSuffix?: string;
  /** Company KPIs the indicator implies (revenue per Kleinunternehmen → small companies). */
  impliedKpis?: readonly KpiId[];
  /** Row keys the index templates read, with their values for this indicator. */
  rowLabels?: Readonly<Record<string, string>>;
  /** Initial value of the metric's indicator filter (otherwise the first option). */
  isDefault?: boolean;
}

/** Ordered as in the dropdowns. */
export const INDEX_INDICATOR_OPTIONS: readonly IndexIndicatorOption[] = [
  /* Companies revenue */
  {
    value: 'revenue_per_company__k_eur',
    metricId: Metric.CompaniesRevenue,
    label: 'Umsatz pro Unternehmen',
    headingLabel: 'Der Umsätze pro Unternehmen',
    column: 'revenue_per_company',
    impliedKpis: ['companies_count', 'companies_revenue'],
    rowLabels: { revenue_index_name: 'Unternehmen' },
  },
  {
    value: 'revenue_per_small_company__k_eur',
    metricId: Metric.CompaniesRevenue,
    label: 'Umsatz pro Kleinunternehmen',
    headingLabel: 'Der Umsätze pro Kleinunternehmen',
    column: 'revenue_per_small_company',
    impliedKpis: ['small_companies_count', 'small_companies_revenue'],
    rowLabels: { revenue_index_name: 'Kleinunternehmen' },
  },

  /* Gross value added */
  ...([
    ['gross_value_added_production_value_pct', 'Anteil Bruttowertschöpfung am Produktionswert'],
    ['gross_value_added_per_total_company__k_eur', 'Bruttowertschöpfung pro Unternehmen insgesamt'],
    ['gross_value_added_per_employed_total__k_eur', 'Bruttowertschöpfung pro Gesamtbeschäftigten'],
    ['gross_value_added_per_employed_social_security__k_eur', 'Bruttowertschöpfung pro SV-pflichtig Beschäftigten'],
    ['gross_value_added_per_economic_participants_total__k_eur', 'Bruttowertschöpfung pro Erwerbstätigen'],
    ['gross_value_added_per_economic_participants_core_market__k_eur', 'Bruttowertschöpfung pro Kernerwerbstätigen'],
  ] as const).map(([value, label]): IndexIndicatorOption => ({
    value,
    metricId: Metric.GrossValueAdded,
    label,
    headingLabel: label,
    column: value,
    isDefault: value === 'gross_value_added_per_total_company__k_eur' || undefined,
  })),

  /* Economic participants */
  ...([
    ['economic_participants_total_per_company', 'Erwerbstätige pro Unternehmen'],
    ['revenue_per_economic_participants_total__k_eur', 'Umsatz pro Erwerbstätigen'],
    ['economic_participants_total_population_pct', 'Anteil Erwerbstätiger an der Bevölkerung'],
    ['employed_total_economic_participants_total_pct', 'Anteil Beschäftigter an Erwerbstätigen'],
  ] as const).map(([value, label]): IndexIndicatorOption => ({
    value,
    metricId: Metric.EconomicParticipant,
    label,
    headingLabel: label,
    column: value,
  })),

  /* Employed: column is the selected KPI joined with the suffix */
  {
    value: 'employed_per_company',
    metricId: Metric.Employed,
    label: 'Beschäftigte pro Unternehmen',
    headingLabel: 'pro Unternehmen',
    kpiSuffix: 'per_company',
    rowLabels: { employed_index_name: 'pro Unternehmen' },
  },
  {
    value: 'employed_state_pct',
    metricId: Metric.Employed,
    label: 'Anteil Teilmarktbeschäftigte an Beschäftigten (branchenübergreifend)',
    headingLabel: 'AN DER BRANCHENÜBERGREIFENDEN GESAMTBESCHÄFTIGUNG',
    kpiSuffix: 'state_pct',
    rowLabels: { employed_index_name: 'an Gesamtbeschäftigung (branchenübergreifend)' },
  },
  {
    value: 'employed_district_pct',
    metricId: Metric.Employed,
    label: 'Anteil Teilmarktbeschäftigte an Beschäftigung (branchenübergreifend)',
    headingLabel: 'AN DER BRANCHENÜBERGREIFENDEN GESAMTBESCHÄFTIGUNG',
    kpiSuffix: 'district_pct',
    rowLabels: { employed_index_name: 'an Beschäftigung (branchenübergreifend)' },
  },
  {
    value: 'employed_population_pct',
    metricId: Metric.Employed,
    label: 'Anteil Teilmarktbeschaftigte an Bevolkerung',
    headingLabel: 'AN DER BEVÖLKERUNG',
    kpiSuffix: 'population_pct',
    rowLabels: { employed_index_name: 'an Bevolkerung' },
  },
];

export const INDEX_INDICATOR_BY_ID: Readonly<Record<string, IndexIndicatorOption>> = Object.fromEntries(
  INDEX_INDICATOR_OPTIONS.map((option) => [option.value, option]),
);

export function selectedIndicator(filterValues: Map<string, any>): IndexIndicatorOption | undefined {
  return INDEX_INDICATOR_BY_ID[filterValues.get(INDICATOR_FILTER_NAME)];
}

/**
 * Filter values saved before the indicator filter existed carry a legacy index filter
 * name (`employed-index`, …). Adds `indicator` from it; values are unchanged.
 */
export function withLegacyIndicatorFilterValue(filterValues: Map<string, any>): Map<string, any> {
  if (filterValues.get(INDICATOR_FILTER_NAME) != null) {
    return filterValues;
  }
  const legacyName = LEGACY_INDICATOR_FILTER_NAMES.find((name) => filterValues.get(name) != null);
  return legacyName ? new Map([...filterValues, [INDICATOR_FILTER_NAME, filterValues.get(legacyName)]]) : filterValues;
}

/** Dataset column of the selected indicator (`value` source) or of a KPI it implies. */
export function indicatorColumn(
  filterValues: Map<string, any>,
  source: 'value' | { impliedMetric: Metric } = 'value',
): string | undefined {
  const indicator = selectedIndicator(filterValues);
  if (!indicator) {
    return undefined;
  }
  if (source !== 'value') {
    const implied = indicator.impliedKpis?.map((id) => KPI_BY_ID[id]).find((kpi) => kpi.metricId === source.impliedMetric);
    return implied?.valueColumn;
  }
  if (indicator.column) {
    return indicator.column;
  }
  const kpi = selectedKpi(filterValues);
  return kpi && indicator.kpiSuffix ? `${kpi.value}_${indicator.kpiSuffix}` : undefined;
}

/**
 * "Indizierter Wert" for every index metric: a child of the hidden `metric_id` filter.
 * `excluded` drops options a dashboard's dataset does not carry.
 */
export class DashboardIndexIndicatorSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor({ metricId, excluded = [] }: { metricId: Metric; excluded?: readonly string[] }) {
    const options = INDEX_INDICATOR_OPTIONS.filter((option) => !excluded.includes(option.value));
    const metricOptions = options.filter((option) => option.metricId === metricId);

    super({
      name: INDICATOR_FILTER_NAME,
      options: options.map((option) => ({ label: option.label, value: option.value, metric_id: `${option.metricId}` })),
      parentFilterSettings: new SingleSelectParentSelectChildFilterSettings({
        parentFilterName: METRIC_ID_FILTER_NAME,
        filterByKey: 'metric_id',
      }),
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({ label: 'Indizierter Wert' }),
      initialValue: (metricOptions.find((option) => option.isDefault) ?? metricOptions[0])?.value,
    });
  }

  /** Follow `metric_id` while hidden too (see DashboardKpiSingleSelectFilter). */
  override initialize() {
    super.initialize();
    this.options$.subscribe();
  }
}

/** Copies the selected indicator's column (or an implied KPI's value) into `resultColumn`. */
export class IndexIndicatorColumnDataTransformer implements DashboardDataTransformer {
  constructor(private config: { resultColumn: string; source?: 'value' | { impliedMetric: Metric } }) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }
    const column = indicatorColumn(filterValues, this.config.source);
    return data.map((row) => ({
      ...row,
      [this.config.resultColumn]: (column && row[column]) ?? '',
    })) as unknown as DSVRowArray;
  }
}

/** Writes the selected indicator's row labels (`revenue_index_name`, `employed_index_name`). */
export class IndexIndicatorLabelDataTransformer implements DashboardDataTransformer {
  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }
    const labels = selectedIndicator(filterValues)?.rowLabels ?? {};
    return data.map((row) => ({ ...row, ...labels })) as unknown as DSVRowArray;
  }
}
